import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, keyframes, useTheme, type Theme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { FC, useEffect, useMemo, useState } from 'react';
import MiniTrendChart from '../Dashboard/MiniTrendChart';
import { formatFinancialYearSubtitle } from './indianFinancialYear';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

export interface RevenueMetricApi {
  value?: number;
  percentageChange?: number;
  bookingCount?: number;
  averageRevenuePerBooking?: number;
  /** Manual (non-calculated) budget total for period when provided by API. */
  budget?: number;
  trend?: { date: string; value: number }[];
  tooltip?: string;
  titleContext?: string;
}

export interface OrgRevenueRow {
  organizationId: number;
  organizationName: string;
  revenue: {
    daily: RevenueMetricApi;
    mtd: RevenueMetricApi;
    ytd: RevenueMetricApi;
  };
}

/** How period revenue growth is compared on consolidated reports (selected date range). */
export type ConsolidatedComparisonMode = 'PRIOR_PERIOD' | 'YOY';

/** Mirrors {@code LeaderboardTabSummaryDto} from the leaderboard-insights API. */
export interface LeaderboardTabSummaryApi {
  headline?: string | null;
}

/** Mirrors {@code LeaderboardInsightsResponse}; display {@link LeaderboardTabSummaryApi#headline} only. */
export interface LeaderboardInsightsApi {
  topRevenue?: LeaderboardTabSummaryApi | null;
  topGrowth?: LeaderboardTabSummaryApi | null;
  mostBookings?: LeaderboardTabSummaryApi | null;
  declining?: LeaderboardTabSummaryApi | null;
  needsAttention?: LeaderboardTabSummaryApi | null;
}

interface ConsolidatedRevenueByOrgTableProps {
  rows: OrgRevenueRow[];
  loading?: boolean;
  /** When set, MTD column reflects selected consolidated date range and labels reference comparison mode. */
  comparisonMode?: ConsolidatedComparisonMode;
  /** Server-rendered tab headlines from {@code POST /admin/consolidated-reports/leaderboard-insights}. */
  insights?: LeaderboardInsightsApi | null;
}

const TOP_N = 10;
const OTHERS_ORG_ID = -1;

/**
 * Stable label when the API omits {@link OrgRevenueRow#organizationName} (avoids blank leaderboard / alert cells).
 */
export function resolvedOrganizationLabel(
  organizationId: number | null | undefined,
  organizationName: string | null | undefined,
  opts?: { isOthersRow?: boolean },
): string {
  if (opts?.isOthersRow) {
    return (organizationName ?? '').trim() || 'Other organizations';
  }
  const n = (organizationName ?? '').trim();
  if (n) {
    return n;
  }
  if (organizationId != null && organizationId !== OTHERS_ORG_ID && Number.isFinite(Number(organizationId))) {
    return `Organization #${organizationId}`;
  }
  return 'Unnamed organization';
}

type RevenuePeriodKey = 'daily' | 'mtd' | 'ytd';

export type LeaderboardRankTab =
  | 'top_revenue'
  | 'top_growth'
  | 'most_bookings'
  | 'declining'
  | 'needs_attention';

function median(values: number[]): number {
  if (!values.length) {
    return 0;
  }
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 !== 0 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

function periodMetric(row: OrgRevenueRow): RevenueMetricApi | undefined {
  return row.revenue?.mtd;
}

function periodRevenue(row: OrgRevenueRow): number {
  return Number(periodMetric(row)?.value) || 0;
}

function periodGrowth(row: OrgRevenueRow): number | null {
  const g = periodMetric(row)?.percentageChange;
  return g != null && Number.isFinite(Number(g)) ? Number(g) : null;
}

function periodBookings(row: OrgRevenueRow): number {
  return Number(periodMetric(row)?.bookingCount) || 0;
}

const formatINRFull = (value: number | null | undefined) =>
  formatCurrencyWithSymbol(Number(value) || 0);

function previousRevenueFromGrowth(current: number, growthPct: number | null): number | null {
  if (growthPct == null || !Number.isFinite(growthPct) || growthPct <= -99.99) {
    return null;
  }
  return current / (1 + growthPct / 100);
}

/** Decline severity from absolute % drop (negative growth magnitude). */
function declineRiskLevel(
  growthPct: number,
): { key: 'critical' | 'high' | 'medium' | 'low'; label: string } {
  const d = Math.abs(growthPct);
  if (d > 40) {
    return { key: 'critical', label: 'Critical' };
  }
  if (d >= 20) {
    return { key: 'high', label: 'High' };
  }
  if (d >= 10) {
    return { key: 'medium', label: 'Medium' };
  }
  return { key: 'low', label: 'Low' };
}

function growthMomentumLabel(g: number): { label: string; arrows: string } {
  if (g >= 25) {
    return { label: 'Strong', arrows: '↑↑' };
  }
  if (g >= 10) {
    return { label: 'Solid', arrows: '↑' };
  }
  if (g >= 4) {
    return { label: 'Building', arrows: '↑' };
  }
  return { label: 'Early', arrows: '→' };
}

function bookingDropDisplay(row: OrgRevenueRow): string {
  const g = periodGrowth(row);
  const bc = periodBookings(row);
  if (g == null || !Number.isFinite(g) || g >= 0 || g <= -99.99) {
    return '—';
  }
  const prevB = bc / (1 + g / 100);
  if (!(prevB > 0)) {
    return '—';
  }
  const dropP = ((prevB - bc) / prevB) * 100;
  return `−${dropP.toFixed(1)}%`;
}

export function mergeMtdTrends(rows: OrgRevenueRow[]): { date: string; value: number }[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    for (const pt of row.revenue?.mtd?.trend || []) {
      const d = pt.date;
      if (!d) continue;
      map.set(d, (map.get(d) || 0) + (Number(pt.value) || 0));
    }
  }
  const out = [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, value]) => ({ date, value }));
  return out.length ? out : [{ date: '', value: 0 }];
}

const KpiMetricCell: FC<{ metric: RevenueMetricApi | undefined; periodLabel: string }> = ({ metric, periodLabel }) => {
  const revenue = Number(metric?.value) || 0;
  const avg = metric?.averageRevenuePerBooking;
  const pct = metric?.percentageChange;
  const fullTip = metric?.tooltip;

  const bc = metric?.bookingCount;
  let subtext = '—';
  if (bc != null && !Number.isNaN(Number(bc))) {
    const countStr = Number(bc).toLocaleString('en-IN');
    if (Number(bc) > 0 && avg != null && !Number.isNaN(Number(avg))) {
      subtext = `${countStr} . Avg: ${formatINRFull(avg)}`;
    } else {
      subtext = `${countStr} . Avg: —`;
    }
  }

  const pctNum = Number(pct);
  const hasPct = !Number.isNaN(pctNum);
  const isPositive = hasPct && pctNum > 0;
  const isNegative = hasPct && pctNum < 0;
  const pctText = hasPct ? `${Math.abs(pctNum).toFixed(1)}%` : '—';

  const chipBg = isPositive ? '#dcfce7' : isNegative ? '#fee2e2' : '#f3f4f6';
  const chipFg = isPositive ? '#166534' : isNegative ? '#991b1b' : '#4b5563';
  const ArrowIcon = isPositive ? ArrowUpwardRoundedIcon : isNegative ? ArrowDownwardRoundedIcon : null;

  const cellTip = [
    fullTip,
    `Revenue: ${formatINRFull(revenue)}`,
    metric?.budget != null && Number.isFinite(Number(metric.budget)) ? `Budget: ${formatINRFull(metric.budget)}` : null,
    periodLabel,
  ]
    .filter(Boolean)
    .join('\n\n');

  return (
    <Tooltip title={cellTip} placement="top">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.35,
          py: 0.25,
          minWidth: 108,
          cursor: 'default',
        }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 800,
            lineHeight: 1.15,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: -0.02,
          }}>
          {formatINRFull(revenue)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: 'text.secondary',
            lineHeight: 1.2,
            textAlign: 'right',
            maxWidth: 200,
          }}>
          {subtext}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.15,
            mt: 0.15,
            px: 0.85,
            py: 0.15,
            borderRadius: '999px',
            backgroundColor: chipBg,
            color: chipFg,
          }}>
          {ArrowIcon && <ArrowIcon sx={{ fontSize: 14 }} />}
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {pctText}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const MTD_TREND_COLOR = '#2563eb';
const GROWTH_TREND_COLOR = '#15803d';
const DECLINE_TREND_COLOR = '#b91c1c';
/** Operational sparkline for Most Bookings tab */
const BOOKINGS_SPARK_COLOR = '#1d4ed8';

/** Tab panel: fade + slide + subtle scale on tab switch */
const leaderboardTabPanelEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(20px, 8px, 0) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
`;

/** Rows: staggered fade + lift (reorder feel) */
const leaderboardRowEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 14px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const leaderboardCardEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 12px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

function tabPanelMotionSx(): object {
  return {
    animation: `${leaderboardTabPanelEnter} 0.44s cubic-bezier(0.22, 1, 0.36, 1) both`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  };
}

function rowMotionSx(index: number): object {
  const d = Math.min(index, 14);
  return {
    animation: `${leaderboardRowEnter} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both`,
    animationDelay: `${48 + d * 36}ms`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      animationDelay: '0ms',
    },
  };
}

/** Wider column so MTD sparkline is readable per row */
const TREND_COL_SX = {
  width: 168,
  minWidth: 168,
  maxWidth: 220,
  px: 1,
} as const;

function sparklineTooltipTitle(
  points: { date: string; value: number }[],
  isAggregate: boolean,
  comparisonMode?: ConsolidatedComparisonMode | null,
): string {
  if (!points?.length) {
    return comparisonMode ? 'No period trend data.' : 'No MTD trend data.';
  }
  const values = points.map(p => Number(p.value) || 0);
  const n = values.length;
  const last = values[n - 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const scope = comparisonMode
    ? isAggregate
      ? 'Combined cumulative confirmed revenue for all organizations in this summary row over the selected date range.'
      : 'Cumulative confirmed revenue by calendar day across the selected date range (left → right).'
    : isAggregate
      ? 'Combined MTD cumulative revenue for all organizations in this summary row.'
      : 'Month-to-date (MTD) cumulative confirmed revenue by calendar day — full current calendar month (1st through last day; left → right).';
  const totalLabel = comparisonMode ? 'Period total (end of sparkline)' : 'MTD total (end of sparkline)';
  const rangeLabel = comparisonMode ? 'Low / high along the range' : 'Low / high along the MTD build-up';
  const lines = [
    scope,
    '',
    `${totalLabel}: ${formatINRFull(last)}`,
    `${rangeLabel}: ${formatINRFull(min)} – ${formatINRFull(max)}`,
    '',
    comparisonMode
      ? 'Shape shows how revenue accumulated through the selected window (pace vs quieter stretches).'
      : 'Shape shows how revenue accumulated through the month (pace vs quieter stretches).',
  ];
  return lines.join('\n');
}

const RANK_TAB_ITEMS: { value: LeaderboardRankTab; label: string }[] = [
  { value: 'top_revenue', label: 'Top Revenue' },
  { value: 'top_growth', label: 'Top Growth' },
  { value: 'most_bookings', label: 'Most Bookings' },
  { value: 'declining', label: 'Declining' },
  { value: 'needs_attention', label: 'Needs Attention' },
];

/** Picks the pre-rendered headline for the active tab (no client-side metric math). */
function leaderboardInsightHeadline(
  insights: LeaderboardInsightsApi | null | undefined,
  rankTab: LeaderboardRankTab,
): string | null {
  if (!insights) {
    return null;
  }
  const raw = insights as Record<string, LeaderboardTabSummaryApi | null | undefined>;
  const pick = (camel: string, snake: string) => raw[camel] ?? raw[snake];
  let block: LeaderboardTabSummaryApi | null | undefined;
  switch (rankTab) {
    case 'top_revenue':
      block = pick('topRevenue', 'top_revenue');
      break;
    case 'top_growth':
      block = pick('topGrowth', 'top_growth');
      break;
    case 'most_bookings':
      block = pick('mostBookings', 'most_bookings');
      break;
    case 'declining':
      block = pick('declining', 'declining');
      break;
    default:
      block = pick('needsAttention', 'needs_attention');
      break;
  }
  const h = block?.headline;
  return h != null && String(h).trim() ? String(h).trim() : null;
}

/** Matches ConsolidatedReports filter card + ConsolidatedRevenueSummaryBanner KPI shells. */
const REPORTS_INSIGHT_STRIP_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  py: 1.35,
  px: 1.5,
  borderRadius: 2,
  bgcolor: 'common.white',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 3px rgba(16, 24, 40, 0.06)',
} as const;

const LeaderboardInsightStripIcon: FC<{ rankTab: LeaderboardRankTab }> = ({ rankTab }) => {
  const sx = { fontSize: 20 };
  switch (rankTab) {
    case 'top_revenue':
      return <AccountBalanceWalletOutlinedIcon sx={sx} />;
    case 'top_growth':
      return <TrendingUpRoundedIcon sx={sx} />;
    case 'most_bookings':
      return <GroupsRoundedIcon sx={sx} />;
    case 'declining':
      return <ArrowDownwardRoundedIcon sx={sx} />;
    default:
      return <WarningAmberOutlinedIcon sx={sx} />;
  }
};

function periodLabelPhrase(comparisonMode?: ConsolidatedComparisonMode): string {
  return comparisonMode ? 'the selected period' : 'this comparison window';
}

function leaderboardTableShellSx(rankTab: LeaderboardRankTab): object {
  const base = {
    maxHeight: { xs: 420, sm: 520, md: 640 },
    borderRadius: '14px',
    overflow: 'hidden',
  };
  switch (rankTab) {
    case 'top_revenue':
      return {
        ...base,
        border: `1px solid ${alpha('#94a3b8', 0.32)}`,
        background: `linear-gradient(175deg, ${alpha('#f8fafc', 0.97)} 0%, #ffffff 24%, #ffffff 100%)`,
        boxShadow: '0 12px 42px rgba(15, 23, 42, 0.08), 0 1px 0 rgba(255,255,255,0.9) inset',
      };
    case 'top_growth':
      return {
        ...base,
        border: `1px solid ${alpha('#22c55e', 0.42)}`,
        background: `linear-gradient(185deg, ${alpha('#ecfdf5', 0.96)} 0%, #ffffff 22%, #ffffff 100%)`,
        boxShadow: `0 10px 36px ${alpha('#15803d', 0.14)}`,
      };
    case 'most_bookings':
      return {
        ...base,
        border: `1px solid ${alpha('#3b82f6', 0.38)}`,
        background: `linear-gradient(185deg, ${alpha('#eff6ff', 0.94)} 0%, #ffffff 22%, #ffffff 100%)`,
        boxShadow: `0 10px 34px ${alpha('#1d4ed8', 0.11)}`,
      };
    case 'declining':
      return {
        ...base,
        border: `1px solid ${alpha('#f97316', 0.5)}`,
        background: `linear-gradient(185deg, ${alpha('#fff7ed', 0.65)} 0%, ${alpha('#fef2f2', 0.8)} 16%, #ffffff 32%)`,
        boxShadow: `0 10px 36px ${alpha('#dc2626', 0.12)}, 0 0 0 1px ${alpha('#fecaca', 0.45)} inset`,
      };
    default:
      return base;
  }
}

function leaderboardTableHeadSx(rankTab: LeaderboardRankTab): object {
  const cell = {
    backgroundClip: 'padding-box',
  };
  switch (rankTab) {
    case 'top_revenue':
      return { '& .MuiTableCell-root': { ...cell, backgroundColor: alpha('#f1f5f9', 0.98), borderBottom: `1px solid ${alpha('#94a3b8', 0.3)}`, color: '#334155' } };
    case 'top_growth':
      return { '& .MuiTableCell-root': { ...cell, backgroundColor: alpha('#dcfce7', 0.88), borderBottom: `1px solid ${alpha('#22c55e', 0.32)}`, color: '#14532d' } };
    case 'most_bookings':
      return { '& .MuiTableCell-root': { ...cell, backgroundColor: alpha('#dbeafe', 0.88), borderBottom: `1px solid ${alpha('#3b82f6', 0.32)}`, color: '#1e3a8a' } };
    case 'declining':
      return { '& .MuiTableCell-root': { ...cell, backgroundColor: alpha('#ffedd5', 0.92), borderBottom: `1px solid ${alpha('#fb923c', 0.42)}`, color: '#7f1d1d' } };
    default:
      return {};
  }
}

const LeaderboardPremiumEmptyRow: FC<{
  rankTab: LeaderboardRankTab;
  colSpan: number;
  orgMatchCount: number;
  comparisonMode?: ConsolidatedComparisonMode;
}> = ({ rankTab, colSpan, orgMatchCount, comparisonMode }) => {
  const period = periodLabelPhrase(comparisonMode);
  const mapped = orgMatchCount > 0;

  const pack =
    rankTab === 'declining'
      ? {
          Icon: CheckCircleOutlineRoundedIcon,
          title: mapped ? 'Healthy trajectory' : 'Nothing to show yet',
          subtitle: mapped
            ? `No declining organizations during ${period}. Each mapped organization held or improved revenue versus the comparison baseline.`
            : 'Map organizations with access to populate this leaderboard.',
          halo: alpha('#10b981', 0.16),
          icon: '#059669',
          vignette: `radial-gradient(120% 85% at 50% 0%, ${alpha('#d1fae5', 0.55)} 0%, transparent 58%)`,
        }
      : rankTab === 'top_growth'
        ? {
            Icon: TrendingFlatRoundedIcon,
            title: mapped ? 'No growth leaders in this window' : 'Nothing to show yet',
            subtitle: mapped
              ? `No organization posted positive revenue growth for ${period}. Momentum may be flat—worth reviewing mix, pipeline, and seasonality.`
              : 'Map organizations with access to populate this leaderboard.',
            halo: alpha('#22c55e', 0.16),
            icon: '#15803d',
            vignette: `radial-gradient(120% 85% at 50% 0%, ${alpha('#bbf7d0', 0.5)} 0%, transparent 58%)`,
          }
        : rankTab === 'most_bookings'
          ? {
              Icon: InsertChartOutlinedRoundedIcon,
              title: mapped ? 'No booking volume to rank' : 'Nothing to show yet',
              subtitle: mapped
                ? `No confirmed booking counts in ${period} are available to order this view. Try widening dates or refreshing after new confirmations.`
                : 'Map organizations with access to populate this leaderboard.',
              halo: alpha('#3b82f6', 0.15),
              icon: '#1d4ed8',
              vignette: `radial-gradient(120% 85% at 50% 0%, ${alpha('#bfdbfe', 0.55)} 0%, transparent 58%)`,
            }
          : {
              Icon: AutoAwesomeOutlinedIcon,
              title: mapped ? 'Nothing to rank in this view' : 'Nothing to show yet',
              subtitle: mapped
                ? 'Adjust filters or the date range to surface revenue leaders for this portfolio.'
                : 'Map organizations with access to populate this leaderboard.',
              halo: alpha('#64748b', 0.14),
              icon: '#475569',
              vignette: `radial-gradient(120% 85% at 50% 0%, ${alpha('#e2e8f0', 0.7)} 0%, transparent 58%)`,
            };

  const IconCmp = pack.Icon;

  return (
    <TableRow sx={rowMotionSx(0)}>
      <TableCell colSpan={colSpan} sx={{ border: 'none', p: 0 }}>
        <Box
          sx={{
            py: { xs: 4.5, sm: 5.5 },
            px: 2,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            background: pack.vignette,
          }}>
          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 460, mx: 'auto' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: pack.halo,
                border: `1px solid ${alpha(pack.icon, 0.28)}`,
              }}>
              <IconCmp sx={{ fontSize: 30, color: pack.icon }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: -0.25, color: 'text.primary', mb: 0.75 }}>
              {pack.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, fontWeight: 500, fontSize: 14 }}>
              {pack.subtitle}
            </Typography>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};

function applyLeaderboardRanking(
  sourceRows: OrgRevenueRow[],
  tab: LeaderboardRankTab,
): {
  ordered: OrgRevenueRow[];
  tabMatchCount: number;
} {
  const real = sourceRows.filter(r => r.organizationId !== OTHERS_ORG_ID);

  let ordered: OrgRevenueRow[] = [];

  switch (tab) {
    case 'top_revenue':
      ordered = [...real].sort((a, b) => periodRevenue(b) - periodRevenue(a));
      break;
    case 'top_growth': {
      const positive = real.filter(r => {
        const g = periodGrowth(r);
        return g != null && g > 0;
      });
      ordered = [...positive].sort((a, b) => periodGrowth(b)! - periodGrowth(a)!);
      break;
    }
    case 'most_bookings':
      ordered = [...real].sort((a, b) => periodBookings(b) - periodBookings(a));
      break;
    case 'declining':
      ordered = [...real]
        .filter(r => {
          const g = periodGrowth(r);
          return g != null && g < 0;
        })
        .sort((a, b) => periodGrowth(a)! - periodGrowth(b)!);
      break;
    case 'needs_attention': {
      const revs = real.map(periodRevenue);
      const med = median(revs);
      ordered = [...real]
        .filter(r => {
          const g = periodGrowth(r);
          const rev = periodRevenue(r);
          if (g != null && g < 0) {
            return true;
          }
          if (rev < med && (g == null || g < 3)) {
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          const ga = periodGrowth(a);
          const gb = periodGrowth(b);
          const va = ga ?? -1e9;
          const vb = gb ?? -1e9;
          if (va !== vb) {
            return va - vb;
          }
          return periodRevenue(a) - periodRevenue(b);
        });
      break;
    }
    default:
      ordered = [...real];
  }

  const tabMatchCount = ordered.length;

  return { ordered, tabMatchCount };
}

const HeaderLabelWithInfo: FC<{ label: string; info: string; align: 'left' | 'right' | 'center' }> = ({ label, info, align }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      gap: 0.35,
      width: '100%',
    }}>
    <span>{label}</span>
    <Tooltip title={info} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 18, color: 'action.active', cursor: 'help', opacity: 0.8, flexShrink: 0, display: 'block' }} />
    </Tooltip>
  </Box>
);

const HeaderLabelStacked: FC<{ label: string; subtitle: string; info: string; align: 'left' | 'right' | 'center' }> = ({
  label,
  subtitle,
  info,
  align,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      gap: 0.15,
      textAlign: align === 'right' ? 'right' : align === 'center' ? 'center' : 'left',
    }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, justifyContent: 'inherit', width: '100%' }}>
      <Typography component="span" sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Tooltip title={info} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help', opacity: 0.75, flexShrink: 0 }} />
      </Tooltip>
    </Box>
    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 10, lineHeight: 1.2 }}>
      {subtitle}
    </Typography>
  </Box>
);

const MiniContributionBar: FC<{ pct: number | null; isMuted?: boolean }> = ({ pct, isMuted }) => {
  const v = pct == null || !Number.isFinite(pct) ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <Tooltip title={pct == null ? 'No portfolio total for share.' : `${pct.toFixed(2)}% of tab portfolio revenue`} placement="top">
      <Box sx={{ width: '100%', maxWidth: 100, ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.35 }}>
        <LinearProgress
          variant="determinate"
          value={v}
          sx={{
            width: '100%',
            height: 6,
            borderRadius: 999,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: isMuted ? 'text.disabled' : 'primary.main' },
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: isMuted ? 'text.secondary' : 'text.primary' }}>
          {pct == null ? '—' : `${pct.toFixed(1)}%`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

function OrganizationCell({
  row,
  isOthersRow,
}: {
  row: OrgRevenueRow;
  isOthersRow: boolean;
}) {
  const displayName = resolvedOrganizationLabel(row.organizationId, row.organizationName, { isOthersRow });
  return (
    <TableCell sx={{ fontWeight: isOthersRow ? 700 : 600, maxWidth: 280, verticalAlign: 'middle', borderLeft: '1px solid #f1f5f9' }}>
      <Tooltip title={displayName} placement="top-start">
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, py: 0.25 }}>
          <Typography
            component="span"
            sx={{
              display: 'block',
              minWidth: 0,
              flex: 1,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              fontStyle: isOthersRow ? 'italic' : 'normal',
              color: isOthersRow ? 'text.secondary' : 'inherit',
            }}>
            {displayName}
          </Typography>
        </Box>
      </Tooltip>
    </TableCell>
  );
}

function TopRevenueTableRow({
  row,
  rowIndex,
  isOthersRow,
  comparisonMode,
  contributionPct,
  motionIndex,
}: {
  row: OrgRevenueRow;
  rowIndex: number;
  isOthersRow: boolean;
  comparisonMode?: ConsolidatedComparisonMode;
  contributionPct: number | null;
  motionIndex?: number;
}) {
  const { daily, mtd, ytd } = row.revenue || {};
  const trend = mtd?.trend?.length ? mtd.trend : [{ date: '', value: 0 }];
  const growthVs =
    comparisonMode === 'YOY'
      ? 'the same calendar dates last year'
      : 'the immediately previous period of equal length';
  const mtdPeriodLabel = comparisonMode
    ? `Revenue in the selected filter range vs ${growthVs} (confirmed).`
    : 'Full current calendar month vs full prior calendar month (confirmed revenue).';

  return (
    <TableRow
      hover={!isOthersRow}
      sx={{
        transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
        ...(motionIndex !== undefined ? rowMotionSx(motionIndex) : {}),
        ...(isOthersRow && {
          backgroundColor: `${alpha('#64748b', 0.1)} !important`,
          '& td': { borderTop: `2px solid ${alpha('#64748b', 0.28)}`, fontStyle: 'normal' },
        }),
        ...(!isOthersRow && {
          '&:nth-of-type(even)': { bgcolor: alpha('#f1f5f9', 0.55) },
          '&:nth-of-type(odd)': { bgcolor: alpha('#ffffff', 0.98) },
          '&:hover': { bgcolor: alpha('#e2e8f0', 0.65), boxShadow: `inset 0 0 0 1px ${alpha('#94a3b8', 0.2)}` },
        }),
      }}>
      <TableCell
        align="center"
        sx={{
          width: 52,
          minWidth: 52,
          maxWidth: 56,
          verticalAlign: 'middle',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: 'text.secondary',
        }}>
        {rowIndex}
      </TableCell>
      <OrganizationCell row={row} isOthersRow={isOthersRow} />
      <TableCell align="right" sx={{ verticalAlign: 'middle', borderLeft: '1px solid #f1f5f9' }}>
        <MiniContributionBar pct={contributionPct} isMuted={isOthersRow} />
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: 'top', borderLeft: '1px solid #f1f5f9' }}>
        <KpiMetricCell metric={daily} periodLabel="Today vs yesterday (confirmed revenue)." />
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: 'top', borderLeft: '1px solid #f1f5f9' }}>
        <KpiMetricCell metric={mtd} periodLabel={mtdPeriodLabel} />
      </TableCell>
      <TableCell align="right" sx={{ verticalAlign: 'top', borderLeft: '1px solid #f1f5f9' }}>
        <KpiMetricCell
          metric={ytd}
          periodLabel="Revenue from 1 April of the current financial year through today vs the same calendar span in the prior financial year (confirmed revenue)."
        />
      </TableCell>
      <TableCell
        align="center"
        sx={{
          ...TREND_COL_SX,
          py: 0.5,
          verticalAlign: 'middle',
          borderLeft: '1px solid #f1f5f9',
        }}>
        <Tooltip title={sparklineTooltipTitle(trend, isOthersRow, comparisonMode)} placement="left" componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}>
          <Box
            sx={{
              height: 28,
              width: '100%',
              minWidth: 140,
              maxWidth: 200,
              mx: 'auto',
              display: 'block',
            }}>
            <MiniTrendChart data={trend} color={MTD_TREND_COLOR} variant="sparkline" />
          </Box>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function TopGrowthTableRow({
  row,
  rowIndex,
  isOthersRow,
  comparisonMode,
  motionIndex,
}: {
  row: OrgRevenueRow;
  rowIndex: number;
  isOthersRow: boolean;
  comparisonMode?: ConsolidatedComparisonMode;
  motionIndex?: number;
}) {
  const mtd = row.revenue?.mtd;
  const trend = mtd?.trend?.length ? mtd.trend : [{ date: '', value: 0 }];
  const g = periodGrowth(row);
  const cur = periodRevenue(row);
  const prev = previousRevenueFromGrowth(cur, g);
  const mom =
    g != null && g > 0
      ? growthMomentumLabel(g)
      : isOthersRow
        ? { label: 'Combined', arrows: '→' }
        : { label: '—', arrows: '—' };
  const isPositive = g != null && g > 0;

  return (
    <TableRow
      hover={!isOthersRow}
      sx={{
        transition: 'background-color 0.16s ease',
        ...(motionIndex !== undefined ? rowMotionSx(motionIndex) : {}),
        ...(isOthersRow && {
          backgroundColor: `${alpha('#15803d', 0.08)} !important`,
          '& td': { borderTop: `2px solid ${alpha('#22c55e', 0.45)}` },
        }),
        ...(!isOthersRow &&
          isPositive && {
            bgcolor: alpha('#22c55e', 0.07),
            '&:nth-of-type(even)': { bgcolor: alpha('#16a34a', 0.1) },
            '&:hover': { bgcolor: alpha('#16a34a', 0.14), boxShadow: `inset 3px 0 0 ${alpha('#15803d', 0.65)}` },
          }),
        ...(!isOthersRow && !isPositive && { '&:hover': { bgcolor: alpha('#f1f5f9', 0.9) } }),
      }}>
      <TableCell align="center" sx={{ fontWeight: 700, color: isPositive ? 'success.dark' : 'text.secondary', verticalAlign: 'middle' }}>
        {rowIndex}
      </TableCell>
      <OrganizationCell row={row} isOthersRow={isOthersRow} />
      <TableCell align="right" sx={{ borderLeft: '1px solid #f1f5f9', verticalAlign: 'middle' }}>
        <Typography fontWeight={800} color={isPositive ? 'success.dark' : 'text.secondary'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {g == null ? '—' : `+${g.toFixed(1)}%`}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ borderLeft: '1px solid #f1f5f9', verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
        {prev == null ? '—' : formatINRFull(prev)}
      </TableCell>
      <TableCell align="right" sx={{ borderLeft: '1px solid #f1f5f9', verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums', fontWeight: 800 }}>
        {formatINRFull(cur)}
      </TableCell>
      <TableCell align="center" sx={{ borderLeft: '1px solid #f1f5f9', verticalAlign: 'middle' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Typography sx={{ fontWeight: 800, color: isPositive ? 'success.dark' : 'text.secondary', fontSize: 13 }}>{mom.arrows}</Typography>
          <Chip
            size="small"
            label={mom.label}
            sx={{
              fontWeight: 700,
              bgcolor: isPositive ? alpha('#15803d', 0.12) : 'action.hover',
              color: isPositive ? 'success.dark' : 'text.secondary',
            }}
          />
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ ...TREND_COL_SX, borderLeft: '1px solid #f1f5f9', py: 0.5, verticalAlign: 'middle' }}>
        <Tooltip title={sparklineTooltipTitle(trend, isOthersRow, comparisonMode)} placement="left" componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}>
          <Box sx={{ height: 28, width: '100%', minWidth: 140, maxWidth: 200, mx: 'auto' }}>
            <MiniTrendChart data={trend} color={GROWTH_TREND_COLOR} variant="sparkline" />
          </Box>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function MostBookingsTableRow({
  row,
  rowIndex,
  isOthersRow,
  comparisonMode,
  motionIndex,
}: {
  row: OrgRevenueRow;
  rowIndex: number;
  isOthersRow: boolean;
  comparisonMode?: ConsolidatedComparisonMode;
  motionIndex?: number;
}) {
  const mtd = row.revenue?.mtd;
  const trend = mtd?.trend?.length ? mtd.trend : [{ date: '', value: 0 }];
  const bc = periodBookings(row);
  const avg = mtd?.averageRevenuePerBooking;
  const rev = periodRevenue(row);

  return (
    <TableRow
      hover={!isOthersRow}
      sx={{
        transition: 'background-color 0.16s ease',
        ...(motionIndex !== undefined ? rowMotionSx(motionIndex) : {}),
        ...(isOthersRow && {
          backgroundColor: `${alpha('#2563eb', 0.1)} !important`,
          '& td': { borderTop: `2px solid ${alpha('#3b82f6', 0.4)}` },
        }),
        ...(!isOthersRow && {
          '&:nth-of-type(even)': { bgcolor: alpha('#dbeafe', 0.35) },
          '&:nth-of-type(odd)': { bgcolor: alpha('#eff6ff', 0.45) },
          '&:hover': { bgcolor: alpha('#bfdbfe', 0.55), boxShadow: `inset 3px 0 0 ${alpha('#2563eb', 0.55)}` },
        }),
      }}>
      <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.dark', verticalAlign: 'middle' }}>
        {rowIndex}
      </TableCell>
      <OrganizationCell row={row} isOthersRow={isOthersRow} />
      <TableCell align="right" sx={{ borderLeft: '1px solid #f1f5f9', fontWeight: 800, fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' }}>
        {bc.toLocaleString('en-IN')}
      </TableCell>
      <TableCell align="right" sx={{ borderLeft: '1px solid #f1f5f9', fontVariantNumeric: 'tabular-nums', fontWeight: 600, verticalAlign: 'middle' }}>
        {avg != null && Number.isFinite(Number(avg)) ? formatINRFull(avg) : '—'}
      </TableCell>
      <TableCell align="right" sx={{ borderLeft: '1px solid #f1f5f9', fontVariantNumeric: 'tabular-nums', fontWeight: 700, verticalAlign: 'middle' }}>
        {formatINRFull(rev)}
      </TableCell>
      <TableCell align="center" sx={{ ...TREND_COL_SX, borderLeft: '1px solid #f1f5f9', py: 0.5, verticalAlign: 'middle' }}>
        <Tooltip title={sparklineTooltipTitle(trend, isOthersRow, comparisonMode)} placement="left" componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}>
          <Box sx={{ height: 28, width: '100%', minWidth: 140, maxWidth: 200, mx: 'auto' }}>
            <MiniTrendChart data={trend} color={BOOKINGS_SPARK_COLOR} variant="sparkline" />
          </Box>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function DecliningTableRow({
  row,
  rowIndex,
  isOthersRow,
  comparisonMode,
  motionIndex,
}: {
  row: OrgRevenueRow;
  rowIndex: number;
  isOthersRow: boolean;
  comparisonMode?: ConsolidatedComparisonMode;
  motionIndex?: number;
}) {
  const theme = useTheme();
  const mtd = row.revenue?.mtd;
  const trend = mtd?.trend?.length ? mtd.trend : [{ date: '', value: 0 }];
  const g = periodGrowth(row);
  const cur = periodRevenue(row);
  const prev = g != null ? previousRevenueFromGrowth(cur, g) : null;
  const loss = prev != null && g != null && g < 0 && prev > cur ? prev - cur : 0;
  const risk = g != null && g < 0 ? declineRiskLevel(g) : { key: 'low' as const, label: '—' };
  const chipColor =
    risk.key === 'critical'
      ? { bg: alpha(theme.palette.error.dark, 0.12), fg: theme.palette.error.dark, border: alpha(theme.palette.error.main, 0.45) }
      : risk.key === 'high'
        ? { bg: alpha(theme.palette.error.main, 0.1), fg: theme.palette.error.main, border: alpha(theme.palette.error.main, 0.35) }
        : risk.key === 'medium'
          ? { bg: alpha('#c2410c', 0.12), fg: '#9a3412', border: alpha('#ea580c', 0.4) }
          : { bg: theme.palette.action.hover, fg: theme.palette.text.secondary, border: theme.palette.divider };

  return (
    <TableRow
      hover={!isOthersRow}
      sx={{
        transition: 'background-color 0.16s ease',
        ...(motionIndex !== undefined ? rowMotionSx(motionIndex) : {}),
        ...(isOthersRow && {
          backgroundColor: `${alpha('#f97316', 0.08)} !important`,
          '& td': { borderTop: `2px solid ${alpha('#ea580c', 0.55)}` },
        }),
        ...(!isOthersRow &&
          g != null &&
          g < 0 && {
            bgcolor: alpha('#fef2f2', 0.85),
            boxShadow: `inset 4px 0 0 ${alpha('#f97316', 0.95)}`,
            '&:nth-of-type(even)': { bgcolor: alpha('#fee2e2', 0.75) },
            '&:hover': { bgcolor: alpha('#fecaca', 0.55) },
          }),
        ...(!isOthersRow && (g == null || g >= 0) && { '&:hover': { bgcolor: alpha('#fff7ed', 0.9) } }),
      }}>
      <TableCell align="center" sx={{ fontWeight: 800, color: 'error.dark', verticalAlign: 'middle' }}>
        {rowIndex}
      </TableCell>
      <OrganizationCell row={row} isOthersRow={isOthersRow} />
      <TableCell align="right" sx={{ borderLeft: '1px solid #fecaca', verticalAlign: 'middle' }}>
        <Typography fontWeight={900} color="error.dark" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {g == null ? '—' : `${g.toFixed(1)}%`}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ borderLeft: '1px solid #fecaca', verticalAlign: 'middle' }}>
        {g != null && g < 0 ? (
          <>
            <Typography fontWeight={800} color="error.main" sx={{ fontVariantNumeric: 'tabular-nums', display: 'block' }}>
              −{formatINRFull(loss)}
            </Typography>
            {prev != null ? (
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', display: 'block', mt: 0.25 }}>
                {formatINRFull(prev)} → {formatINRFull(cur)}
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography color="text.secondary">—</Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={{ borderLeft: '1px solid #fecaca', verticalAlign: 'middle', fontWeight: 700, color: 'error.dark' }}>
        {g != null && g < 0 ? bookingDropDisplay(row) : '—'}
      </TableCell>
      <TableCell align="center" sx={{ borderLeft: '1px solid #fecaca', verticalAlign: 'middle' }}>
        {g != null && g < 0 ? (
          <Chip
            size="small"
            label={risk.label}
            sx={{
              fontWeight: 800,
              bgcolor: chipColor.bg,
              color: chipColor.fg,
              border: '1px solid',
              borderColor: chipColor.border,
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="center" sx={{ ...TREND_COL_SX, borderLeft: '1px solid #fecaca', py: 0.5, verticalAlign: 'middle' }}>
        <Tooltip title={sparklineTooltipTitle(trend, isOthersRow, comparisonMode)} placement="left" componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}>
          <Box sx={{ height: 28, width: '100%', minWidth: 140, maxWidth: 200, mx: 'auto' }}>
            <MiniTrendChart data={trend} color={DECLINE_TREND_COLOR} variant="sparkline" />
          </Box>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

type AttentionSeverity = 'critical' | 'high' | 'medium';

type AttentionCardModel = {
  organizationId: number;
  organizationName: string;
  issue: string;
  severity: AttentionSeverity;
  IssueIcon: React.ElementType;
};

function buildAttentionCards(rows: OrgRevenueRow[], portfolioMedian: number): AttentionCardModel[] {
  const real = rows.filter(r => r.organizationId !== OTHERS_ORG_ID);
  const med = portfolioMedian;
  const out: AttentionCardModel[] = [];

  for (const r of real) {
    const g = periodGrowth(r);
    const rev = periodRevenue(r);
    const bc = periodBookings(r);

    if (bc === 0 && rev === 0) {
      out.push({
        organizationId: r.organizationId,
        organizationName: resolvedOrganizationLabel(r.organizationId, r.organizationName),
        issue: 'No bookings in period — pipeline may be stalled.',
        severity: 'high',
        IssueIcon: EventBusyOutlinedIcon,
      });
      continue;
    }

    if (g != null && g < 0) {
      const sev: AttentionSeverity = g <= -25 ? 'critical' : g <= -12 ? 'high' : 'medium';
      const sharp = g <= -20;
      out.push({
        organizationId: r.organizationId,
        organizationName: resolvedOrganizationLabel(r.organizationId, r.organizationName),
        issue: sharp
          ? 'High cancellation / slippage — revenue falling faster than peers.'
          : 'Payment risk — revenue down vs comparison period.',
        severity: sev,
        IssueIcon: sharp ? CancelOutlinedIcon : AccountBalanceWalletOutlinedIcon,
      });
    }

    if (rev < med && (g == null || g < 3)) {
      out.push({
        organizationId: r.organizationId,
        organizationName: resolvedOrganizationLabel(r.organizationId, r.organizationName),
        issue: 'Pace below target — trailing portfolio median.',
        severity: 'medium',
        IssueIcon: SpeedOutlinedIcon,
      });
    }

    if (g != null && g < 0 && g > -15 && rev < med) {
      out.push({
        organizationId: r.organizationId,
        organizationName: resolvedOrganizationLabel(r.organizationId, r.organizationName),
        issue: 'Conversion below average — win rate likely softening.',
        severity: 'medium',
        IssueIcon: QueryStatsOutlinedIcon,
      });
    }
  }

  const dedup = new Map<string, AttentionCardModel>();
  for (const c of out) {
    const k = `${c.organizationId}-${c.issue}`;
    dedup.set(k, c);
  }
  return [...dedup.values()].slice(0, 24);
}

function attentionChipProps(sev: AttentionSeverity, theme: Theme) {
  if (sev === 'critical') {
    return {
      label: 'Critical',
      bgcolor: alpha(theme.palette.error.dark, 0.14),
      color: theme.palette.error.dark,
      borderColor: alpha(theme.palette.error.dark, 0.55),
    };
  }
  if (sev === 'high') {
    return {
      label: 'High',
      bgcolor: alpha(theme.palette.error.main, 0.12),
      color: '#b91c1c',
      borderColor: alpha(theme.palette.error.main, 0.45),
    };
  }
  return {
    label: 'Medium',
    bgcolor: alpha('#ea580c', 0.12),
    color: '#9a3412',
    borderColor: alpha('#ea580c', 0.4),
  };
}

function NeedsAttentionPanel({
  cards,
  loading,
  emptyTitle,
  emptySubtitle,
}: {
  cards: AttentionCardModel[];
  loading: boolean;
  emptyTitle: string;
  emptySubtitle: string;
}) {
  const theme = useTheme();
  if (loading) {
    return (
      <Stack spacing={1.25}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: 2, ...rowMotionSx(i) }} />
        ))}
      </Stack>
    );
  }
  if (!cards.length) {
    return (
      <Box
        sx={{
          py: 4.5,
          px: 2.5,
          textAlign: 'center',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          border: `1px dashed ${alpha('#f59e0b', 0.55)}`,
          background: `linear-gradient(165deg, ${alpha('#fef3c7', 0.55)} 0%, ${alpha('#fee2e2', 0.35)} 48%, ${alpha('#fffbeb', 0.4)} 100%)`,
          boxShadow: `inset 0 0 0 1px ${alpha('#ef4444', 0.08)}`,
        }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            mx: 'auto',
            mb: 1.25,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha('#f59e0b', 0.18),
            border: `1px solid ${alpha('#ea580c', 0.35)}`,
          }}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 30, color: 'warning.dark' }} />
        </Box>
        <Typography variant="subtitle1" fontWeight={800} color="text.primary" sx={{ mb: 0.75, letterSpacing: -0.2 }}>
          {emptyTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.65, maxWidth: 440, mx: 'auto' }}>
          {emptySubtitle}
        </Typography>
      </Box>
    );
  }
  return (
    <Stack spacing={1.25}>
      {cards.map((c, i) => {
        const Icon = c.IssueIcon;
        const chipProps = attentionChipProps(c.severity, theme);
        return (
          <Card
            key={`${c.organizationId}-${c.issue}`}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.45),
              borderLeftWidth: 5,
              borderLeftColor: c.severity === 'critical' ? theme.palette.error.main : c.severity === 'high' ? '#ea580c' : '#f59e0b',
              borderRadius: 2,
              bgcolor: alpha('#fffbeb', 0.85),
              boxShadow: '0 2px 10px rgba(180, 83, 9, 0.08)',
              animation: `${leaderboardCardEnter} 0.42s cubic-bezier(0.22, 1, 0.36, 1) both`,
              animationDelay: `${96 + Math.min(i, 12) * 48}ms`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                animationDelay: '0ms',
              },
            }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  alignItems: 'start',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px,1fr) minmax(0,2fr) auto' },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Tooltip title={c.organizationName} placement="top-start" arrow>
                    <Typography fontWeight={800} noWrap sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.organizationName}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                  <WarningAmberOutlinedIcon sx={{ color: 'warning.dark', fontSize: 22, flexShrink: 0, mt: 0.15 }} />
                  <Icon sx={{ color: 'warning.main', fontSize: 20, flexShrink: 0, mt: 0.2, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.45 }}>
                    {c.issue}
                  </Typography>
                </Box>
                <Box sx={{ justifySelf: { xs: 'start', sm: 'center' } }}>
                  <Chip
                    size="small"
                    label={chipProps.label}
                    sx={{
                      fontWeight: 800,
                      bgcolor: chipProps.bgcolor,
                      color: chipProps.color,
                      border: '1px solid',
                      borderColor: chipProps.borderColor,
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

const ConsolidatedRevenueByOrgTable: FC<ConsolidatedRevenueByOrgTableProps> = ({
  rows,
  loading,
  comparisonMode,
  insights,
}) => {
  const [rankTab, setRankTab] = useState<LeaderboardRankTab>('top_revenue');
  const [leaderboardViewAll, setLeaderboardViewAll] = useState(false);

  useEffect(() => {
    setLeaderboardViewAll(false);
  }, [rankTab]);

  const dateSubtitles = useMemo(
    () => ({
      today: dayjs().format('ddd, D MMM YYYY'),
      monthRange: `${dayjs().startOf('month').format('D MMM')} – ${dayjs().endOf('month').format('D MMM YYYY')}`,
      yearRange: formatFinancialYearSubtitle(),
    }),
    [],
  );

  const rankTrendMeta = useMemo(() => {
    if (!comparisonMode) {
      return {
        trendLabel: 'Month trend',
        trendInfo:
          'Cumulative confirmed revenue by calendar day for the full current calendar month (sparkline; same window as Months revenue).',
      };
    }
    const cmp =
      comparisonMode === 'YOY'
        ? 'Growth compares to the same calendar dates last year.'
        : 'Growth compares to the immediately previous period of equal length (confirmed).';
    return {
      trendLabel: 'Period trend',
      trendInfo: `Cumulative confirmed revenue by day across the selected filter range (sparkline). ${cmp}`,
    };
  }, [comparisonMode]);

  const orgMatchCount = useMemo(
    () => rows.filter(r => r.organizationId !== OTHERS_ORG_ID).length,
    [rows],
  );

  const portfolioMedian = useMemo(() => {
    const revs = rows.filter(r => r.organizationId !== OTHERS_ORG_ID).map(periodRevenue);
    return median(revs);
  }, [rows]);

  const tabCounts = useMemo(() => {
    const real = rows.filter(r => r.organizationId !== OTHERS_ORG_ID);
    const revs = real.map(periodRevenue);
    const med = median(revs);
    const declining = real.filter(r => {
      const g = periodGrowth(r);
      return g != null && g < 0;
    }).length;
    const topGrowth = real.filter(r => {
      const g = periodGrowth(r);
      return g != null && g > 0;
    }).length;
    const needs = real.filter(r => {
      const g = periodGrowth(r);
      const rev = periodRevenue(r);
      if (g != null && g < 0) {
        return true;
      }
      if (rev < med && (g == null || g < 3)) {
        return true;
      }
      return false;
    }).length;
    return { all: real.length, declining, needs, topGrowth };
  }, [rows]);

  const rankingResult = useMemo(() => applyLeaderboardRanking(rows, rankTab), [rows, rankTab]);

  const displayRows = useMemo(() => {
    const { ordered } = rankingResult;
    if (leaderboardViewAll || ordered.length <= TOP_N) {
      return ordered;
    }
    return ordered.slice(0, TOP_N);
  }, [rankingResult, leaderboardViewAll]);

  const leaderboardHasMore = rankingResult.tabMatchCount > TOP_N;

  const tabRevenueTotal = useMemo(
    () => displayRows.reduce((s, r) => s + periodRevenue(r), 0),
    [displayRows],
  );

  const attentionCards = useMemo(() => buildAttentionCards(rows, portfolioMedian), [rows, portfolioMedian]);

  const skeletonRowCount = 8;
  const colCount =
    rankTab === 'most_bookings' ? 6 : rankTab === 'needs_attention' ? 1 : 7;

  const headCellSx = {
    fontWeight: 700,
    position: 'sticky' as const,
    top: 0,
    zIndex: 6,
    whiteSpace: 'nowrap' as const,
  };

  const body = useMemo(() => {
    const pctForTabRowInner = (row: OrgRevenueRow): number | null =>
      tabRevenueTotal <= 0 ? null : (100 * periodRevenue(row)) / tabRevenueTotal;

    if (rankTab === 'needs_attention') {
      return null;
    }

    if (loading) {
      return Array.from({ length: skeletonRowCount }).map((_, i) => (
        <TableRow key={`sk-${i}`} sx={rowMotionSx(i)}>
          {Array.from({ length: colCount }).map((__, j) => (
            <TableCell
              key={j}
              align={j === 0 ? 'center' : j === 1 ? 'left' : j === colCount - 1 ? 'center' : 'right'}
              sx={{
                py: 1.25,
                verticalAlign: j === 0 || j === 1 || j === 2 ? 'middle' : 'top',
                ...(j === colCount - 1 ? TREND_COL_SX : {}),
              }}>
              {j === 0 ? (
                <Skeleton variant="text" width={20} sx={{ mx: 'auto' }} />
              ) : j === colCount - 1 ? (
                <Skeleton variant="rounded" width={Math.min(TREND_COL_SX.minWidth, 160)} height={28} sx={{ mx: 'auto', borderRadius: 1 }} />
              ) : (
                <>
                  <Skeleton variant="text" width={j === 1 ? '70%' : j === 2 ? '50%' : '85%'} sx={{ ml: j === 1 ? 0 : 'auto' }} />
                  {j > 2 && rankTab === 'top_revenue' && (
                    <>
                      <Skeleton variant="text" width="60%" sx={{ ml: 'auto', mt: 0.5 }} />
                      <Skeleton variant="rounded" width={52} height={20} sx={{ ml: 'auto', mt: 0.5, borderRadius: 999 }} />
                    </>
                  )}
                  {(rankTab === 'top_growth' || rankTab === 'declining') && j > 2 && j < colCount - 1 && (
                    <Skeleton variant="text" width="70%" sx={{ ml: 'auto', mt: 0.5 }} />
                  )}
                </>
              )}
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (!displayRows.length) {
      return (
        <LeaderboardPremiumEmptyRow
          rankTab={rankTab}
          colSpan={colCount}
          orgMatchCount={orgMatchCount}
          comparisonMode={comparisonMode}
        />
      );
    }

    return displayRows.map((row: OrgRevenueRow, idx: number) => {
      const common = {
        key: `${row.organizationId}-${idx}`,
        row,
        rowIndex: idx + 1,
        isOthersRow: row.organizationId === OTHERS_ORG_ID,
        comparisonMode,
        motionIndex: idx,
      };
      if (rankTab === 'top_revenue') {
        return <TopRevenueTableRow {...common} contributionPct={pctForTabRowInner(row)} />;
      }
      if (rankTab === 'top_growth') {
        return <TopGrowthTableRow {...common} />;
      }
      if (rankTab === 'most_bookings') {
        return <MostBookingsTableRow {...common} />;
      }
      return <DecliningTableRow {...common} />;
    });
  }, [loading, displayRows, rankTab, orgMatchCount, comparisonMode, colCount, tabRevenueTotal]);

  const tableHead =
    rankTab === 'top_revenue' ? (
      <TableRow>
        <TableCell align="center" sx={{ ...headCellSx, width: 52, minWidth: 52, maxWidth: 56, verticalAlign: 'middle' }}>
          #
        </TableCell>
        <TableCell sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', verticalAlign: 'middle' }}>Organization</TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', minWidth: 112 }}>
          <HeaderLabelWithInfo
            label="Contribution"
            info="Share of period revenue among organizations shown in this table (top 10 by default; use View all for the full ranked list)."
            align="right"
          />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelStacked
            label="Today's Revenue"
            subtitle={dateSubtitles.today}
            info="Confirmed revenue for the server’s current calendar day vs the prior day (bookings + growth chip)."
            align="right"
          />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelStacked
            label="Months Revenue"
            subtitle={dateSubtitles.monthRange}
            info={
              comparisonMode
                ? 'Revenue in the selected filter range vs comparison mode (see portfolio filter).'
                : 'Full current calendar month vs full prior calendar month (confirmed).'
            }
            align="right"
          />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelStacked
            label="Years Revenue"
            subtitle={dateSubtitles.yearRange}
            info="Revenue from 1 April of the current financial year (Apr–Mar) through today vs the same calendar span in the prior financial year (confirmed)."
            align="right"
          />
        </TableCell>
        <TableCell align="center" sx={{ ...headCellSx, ...TREND_COL_SX, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelWithInfo label={rankTrendMeta.trendLabel} info={rankTrendMeta.trendInfo} align="center" />
        </TableCell>
      </TableRow>
    ) : rankTab === 'top_growth' ? (
      <TableRow>
        <TableCell align="center" sx={{ ...headCellSx, color: 'success.dark' }}>
          #
        </TableCell>
        <TableCell sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', color: 'success.dark' }}>Organization</TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', color: 'success.dark' }}>
          <HeaderLabelWithInfo label="Growth %" info="Period growth % (same basis as Months revenue)." align="right" />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', color: 'success.dark' }}>
          <HeaderLabelWithInfo label="Previous Revenue" info="Implied prior-period revenue from current revenue and growth %." align="right" />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', color: 'success.dark' }}>
          <HeaderLabelWithInfo label="Current Revenue" info="Revenue in the comparison period (Months / filter range)." align="right" />
        </TableCell>
        <TableCell align="center" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', color: 'success.dark' }}>
          <HeaderLabelWithInfo label="Momentum" info="Visual momentum from growth magnitude (arrows + band)." align="center" />
        </TableCell>
        <TableCell align="center" sx={{ ...headCellSx, ...TREND_COL_SX, borderLeft: '1px solid #e5e7eb', color: 'success.dark' }}>
          <HeaderLabelWithInfo label="Trend" info={rankTrendMeta.trendInfo} align="center" />
        </TableCell>
      </TableRow>
    ) : rankTab === 'most_bookings' ? (
      <TableRow>
        <TableCell align="center" sx={{ ...headCellSx, width: 52, minWidth: 52, maxWidth: 56, verticalAlign: 'middle' }}>
          #
        </TableCell>
        <TableCell sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb', verticalAlign: 'middle' }}>Organization</TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelWithInfo label="Bookings" info="Confirmed booking count in the same window as Months revenue." align="right" />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelWithInfo label="Avg Booking" info="Average confirmed revenue per booking in that window." align="right" />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelWithInfo label="Revenue" info="Total confirmed revenue in the same window." align="right" />
        </TableCell>
        <TableCell align="center" sx={{ ...headCellSx, ...TREND_COL_SX, borderLeft: '1px solid #e5e7eb' }}>
          <HeaderLabelWithInfo label="Trend" info={rankTrendMeta.trendInfo} align="center" />
        </TableCell>
      </TableRow>
    ) : (
      <TableRow>
        <TableCell align="center" sx={{ ...headCellSx, color: 'error.dark' }}>
          #
        </TableCell>
        <TableCell sx={{ ...headCellSx, borderLeft: '1px solid #fecaca', color: 'error.dark' }}>Organization</TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #fecaca', color: 'error.dark' }}>
          <HeaderLabelWithInfo label="Revenue Decline" info="Negative growth % for the comparison period." align="right" />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #fecaca', color: 'error.dark' }}>
          <HeaderLabelWithInfo
            label="Revenue Loss"
            info="Estimated revenue no longer booked vs implied prior period; comparison strip shows prior → current compact amounts."
            align="right"
          />
        </TableCell>
        <TableCell align="right" sx={{ ...headCellSx, borderLeft: '1px solid #fecaca', color: 'error.dark' }}>
          <HeaderLabelWithInfo
            label="Booking Drop"
            info="Approximate booking-volume drop implied by the same growth % (proxy when booking-level history is unavailable)."
            align="right"
          />
        </TableCell>
        <TableCell align="center" sx={{ ...headCellSx, borderLeft: '1px solid #fecaca', color: 'error.dark' }}>
          <HeaderLabelWithInfo
            label="Risk Level"
            info="Critical if decline >40%, High for 20–40%, Medium for 10–20%, else Low."
            align="center"
          />
        </TableCell>
        <TableCell align="center" sx={{ ...headCellSx, ...TREND_COL_SX, borderLeft: '1px solid #fecaca', color: 'error.dark' }}>
          <HeaderLabelWithInfo label="Trend" info={rankTrendMeta.trendInfo} align="center" />
        </TableCell>
      </TableRow>
    );

  const needsEmptyCopy = useMemo(
    () => ({
      title: orgMatchCount > 0 ? 'All clear on this portfolio slice' : 'Nothing to flag yet',
      subtitle:
        orgMatchCount > 0
          ? 'No rule-based alerts fired for this selection. Organizations stayed outside “needs attention” thresholds for the current metrics.'
          : 'Map organizations with access to evaluate portfolio health signals.',
    }),
    [orgMatchCount],
  );

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Tabs
          value={rankTab}
          onChange={(_e, v: LeaderboardRankTab | false) => {
            if (v !== false) {
              setRankTab(v);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 42,
            '& .MuiTab-root': { minHeight: 42, py: 1, fontSize: 13, fontWeight: 700, textTransform: 'none' },
            '& .MuiTabs-indicator': {
              transition: 'left 0.35s cubic-bezier(0.22, 1, 0.36, 1), width 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
            },
          }}>
          {RANK_TAB_ITEMS.map(t => {
            const extra =
              t.value === 'declining'
                ? ` (${tabCounts.declining})`
                : t.value === 'needs_attention'
                  ? ` (${tabCounts.needs})`
                  : t.value === 'top_growth'
                    ? ` (${tabCounts.topGrowth})`
                    : ` (${tabCounts.all})`;
            return <Tab key={t.value} value={t.value} label={`${t.label}${extra}`} />;
          })}
        </Tabs>
      </Box>

      <Box sx={{ mb: 1.25, ...REPORTS_INSIGHT_STRIP_SX }}>
        {loading ? (
          <Skeleton variant="rounded" height={52} sx={{ borderRadius: 1.5, width: '100%' }} />
        ) : (
          <>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
              <LeaderboardInsightStripIcon rankTab={rankTab} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.35,
                  lineHeight: 1.2,
                  display: 'block',
                }}>
                {RANK_TAB_ITEMS.find(t => t.value === rankTab)?.label ?? 'Insight'}
              </Typography>
              <Typography
                key={rankTab}
                variant="body2"
                sx={{
                  mt: 0.35,
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.45,
                }}>
                {leaderboardInsightHeadline(insights, rankTab) ??
                  'Insights for this range will appear here when available.'}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Box key={rankTab} sx={tabPanelMotionSx()}>
        {rankTab === 'needs_attention' ? (
        <Box
          sx={{
            border: `1px solid ${alpha('#f59e0b', 0.5)}`,
            borderRadius: 2,
            p: 2,
            background: `linear-gradient(135deg, ${alpha('#fffbeb', 0.98)} 0%, ${alpha('#fef2f2', 0.55)} 42%, ${alpha('#fff7ed', 0.75)} 100%)`,
            boxShadow: `0 8px 32px ${alpha('#f59e0b', 0.12)}, 0 0 0 1px ${alpha('#ef4444', 0.12)} inset`,
          }}>
          <Typography variant="subtitle2" fontWeight={800} color="warning.dark" sx={{ mb: 0.5 }}>
            Portfolio alerts
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Rule-based signals from this leaderboard’s metrics — not a standard ranking table.
          </Typography>
          <Box
            sx={{
              display: { xs: 'none', sm: 'grid' },
              gridTemplateColumns: 'minmax(140px,1fr) minmax(0,2fr) auto',
              gap: 1,
              mb: 1,
              px: 0.5,
            }}>
            <Typography variant="caption" fontWeight={800} color="text.secondary">
              Organization
            </Typography>
            <Typography variant="caption" fontWeight={800} color="text.secondary">
              Issue
            </Typography>
            <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textAlign: 'center' }}>
              Severity
            </Typography>
          </Box>
          <NeedsAttentionPanel
            cards={attentionCards}
            loading={!!loading}
            emptyTitle={needsEmptyCopy.title}
            emptySubtitle={needsEmptyCopy.subtitle}
          />
        </Box>
      ) : (
        <>
          <TableContainer sx={leaderboardTableShellSx(rankTab)}>
            <Table size="small" stickyHeader>
              <TableHead sx={leaderboardTableHeadSx(rankTab)}>{tableHead}</TableHead>
              <TableBody>{body}</TableBody>
            </Table>
          </TableContainer>
          {leaderboardHasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.25, pb: 0.5 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setLeaderboardViewAll(v => !v)}
                sx={{ fontWeight: 700, textTransform: 'none' }}>
                {leaderboardViewAll ? `Show top ${TOP_N}` : `View all (${rankingResult.tabMatchCount})`}
              </Button>
            </Box>
          )}
        </>
        )}
      </Box>
    </Box>
  );
};

export default ConsolidatedRevenueByOrgTable;
